import axios from "axios";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button } from "antd";
import { ReactComponent as ExportIcon } from "../../assets/upload-edit.svg";
import { baseURL } from "../../globalUtils/axiosConfig";
import { fetchMaterialTypes } from '../materialTypes/redux/actions';
import { fetchAllTemplates } from "../materialTemplates/redux/actions";
import ProgressSummaryReporting from "./ProgressSummaryReporting";
import ProgressSummaryReportingDetails from "./ProgressSummaryReportingDetails";
import "./index.less";

const ProgressSummary = () => {
  const [projectDetails, setProjectDetails] = useState(null);
  const dispatch = useDispatch();
  const { project_id } = useParams();
  const [priorityAreaStats, setPriorityAreaStats] = useState(null);
  const [priorityAreaLoading, setPriorityAreaLoading] = useState(false);
  const [priorityAreaError, setPriorityAreaError] = useState(null);

  const fetchProjectDetails = async () => {
    if (!project_id) {
      return;
    }
    
    try {
      const res = await axios.get(`${baseURL}/project/${project_id}`);
      
      if (res.status === 200) {
        if (res.data && res.data.data) {
          setProjectDetails(res.data.data);
        } else if (res.data) {
          setProjectDetails(res.data);
        }
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  useEffect(() => {
    if (project_id) {
      fetchProjectDetails();
      fetchProgressSummaryData();
      fetchPriorityAreaStats();
    }
  }, [project_id]);
  
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProgressSummaryData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the real API endpoint to fetch progress summary data
      const response = await axios.get(`${baseURL}/material-control-registers/completed-total-gross-stats?projectId=${project_id}`);
      
      if (response.status === 200 && response.data) {
        setProgressData(response.data);
        console.log('Progress data fetched successfully for project:', project_id);
      } else {
        throw new Error('Invalid response format');
      }

    } catch (error) {
      console.error('Error fetching progress summary data:', error);
      setError('Failed to fetch progress data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPriorityAreaStats = async () => {
    setPriorityAreaLoading(true);
    setPriorityAreaError(null);
    try {
      // Fetch priority and area stats data
      const response = await axios.get(`${baseURL}/material-control-registers/completed-total-gross-stats-by-priority-and-area?projectId=${project_id}`);
      
      if (response.status === 200 && response.data) {
        setPriorityAreaStats(response.data);
        console.log('Priority and area stats fetched successfully for project:', project_id);
      } else {
        throw new Error('Invalid response format');
      }

    } catch (error) {
      console.error('Error fetching priority and area stats:', error);
      setPriorityAreaError('Failed to fetch priority and area stats. Please try again later.');
    } finally {
      setPriorityAreaLoading(false);
    }
  };

  const formatAsAtDate = () => {
    try {
      return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(',', '');
    } catch {
      return '';
    }
  };

  const csvEscape = (value) => {
    const s = String(value ?? "");
    return '"' + s.replace(/"/g, '""') + '"';
  };

  const formatDateStamp = () => {
    try {
      return new Date().toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  const buildSummaryCsv = (data) => {
    if (!data) return "";
    const headers = [
      "Description",
      "Total / Gross",
      "FABRICATION",
      "FABRICATION %",
      "QC Release",
      "QC Release %",
      "CORROS PROT/ DEL. WRKSHP",
      "CORROS PROT/ DEL. WRKSHP %",
      "DEL SITE",
      "DEL SITE %",
      "SITE RECEIPT",
      "SITE RECEIPT %",
      "INSTALLATION",
      "INSTALLATION %",
    ];

    const rows = Object.keys(data).map((key) => {
      const item = data[key] || {};
      const description = `${key.toUpperCase()} - ${(item.uom || '').toLowerCase()}`;
      const fabrication = item.fabrication || {};
      const qc = item.qc_release || {};
      const corr = item.corrosion_protection || {};
      const del = item.delivery_to_site || {};
      const site = item.site_receipt || {};
      const installation = item.installation || {};
      return [
        description,
        item["Total / Gross"] ?? 0,
        fabrication.totalGross ?? 0,
        fabrication.percentageCompleted ?? 0,
        qc.totalGross ?? 0,
        qc.percentageCompleted ?? 0,
        corr.totalGross ?? 0,
        corr.percentageCompleted ?? 0,
        del.totalGross ?? 0,
        del.percentageCompleted ?? 0,
        site.totalGross ?? 0,
        site.percentageCompleted ?? 0,
        installation.totalGross ?? 0,
        installation.percentageCompleted ?? 0,
      ];
    });

    const csv = [headers.map(csvEscape).join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join("\n");
    return "\ufeff" + csv;
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${baseURL}/material-control-registers/export-summary-report?projectId=${project_id}`, {
        responseType: 'blob', // Important for file downloads
      });

      // Create blob from response
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      
      // Generate filename
      const proj = projectDetails?.projectNumber || project_id || "project";
      const stamp = formatDateStamp();
      
      // Try to get filename from response headers, fallback to generated name
      const contentDisposition = response.headers['content-disposition'];
      let filename = `progress_summary_${proj}_${stamp}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting summary report:', error);
      // Optionally show user-friendly error message
      // message.error('Failed to export summary report. Please try again.');
    }
  };

  // Function to render multiple instances of ProgressSummaryReportingDetails
  // with different filters if needed
  const renderDetailedComponents = () => {
    if (!priorityAreaStats) return null;
    
    // You can add logic here to filter or transform the data differently for each instance
    // For example, you might want to show different material types in different components
    
    // Get unique material types from the data
    const materialTypes = Object.keys(priorityAreaStats);
    
    return materialTypes.map((materialType, index) => {
      // Create a filtered version of the data that only includes this material type
      const filteredData = { [materialType]: priorityAreaStats[materialType] };
      
      // Get the UOM for this material type if it exists
      const uom = priorityAreaStats[materialType]?.uom || '';
      // Format title with material type in uppercase and UOM in lowercase using JSX
      const titleWithUom = uom ? (
        <>
          {materialType.toUpperCase()} (<span className="uom-text">{uom.toLowerCase()}</span>)
        </>
      ) : materialType.toUpperCase();
      
      return (
        <ProgressSummaryReportingDetails 
          key={`details-${materialType}-${index}`}
          project_id={project_id} 
          progressData={filteredData}
          loading={priorityAreaLoading} 
          error={priorityAreaError} 
          projectDetails={projectDetails}
          title={titleWithUom}
          materialType={materialType}
        />
      );
    });
  };

  return (
    <div className="page-top-div">
      <div className="top-bar">
        <span className="text-span">Progress Summary Report for Project  {projectDetails?.projectNumber} - {projectDetails?.client}</span>
        <div className="right-info">
          <span className="status-date">Status as at {formatAsAtDate()}</span>
          <Button icon={<ExportIcon className="export-icon" />} onClick={handleExport} style={{backgroundColor: "transparent", border: "none"}} />
        </div>
      </div>
      <ProgressSummaryReporting 
        project_id={project_id} 
        progressData={progressData} 
        loading={loading} 
        error={error} 
        projectDetails={projectDetails}
      />
      {renderDetailedComponents()}
    </div>
  );
};

export default ProgressSummary;